import React, { useState, useEffect } from "react";
import {
  Shield,
  TrendingUp,
  Clock,
  Users,
  Award,
  CheckCircle,
  ArrowRight,
  Star,
  DollarSign,
  Lock,
  BarChart3,
} from "lucide-react";
import "../styles/home.css";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // maintain route state; default to login
  const [route, setRoute] = useState("/login");

  // update route once auth resolves, without blocking initial render
  useEffect(() => {
    if (!loading) {
      const target = user
        ? user.name === "Admin"
          ? "/admin/dashboard"
          : `/${user.userId}`
        : "/login";
      setRoute(target);
    }
  }, [user, loading]);

  const handleNavigate = () => {
    navigate(route);
  };

  const features = [
    {
      icon: <Shield className="feature-icon-svg" />,
      title: "Secure Investments",
      description:
        "Your capital is protected with bank-grade security measures and regulatory compliance.",
    },
    {
      icon: <TrendingUp className="feature-icon-svg" />,
      title: "Competitive Returns",
      description:
        "Earn up to 12% annual returns on your fixed deposit investments with guaranteed payouts.",
    },
    {
      icon: <Clock className="feature-icon-svg" />,
      title: "Flexible Tenure",
      description:
        "Choose from 6 months to 5 years investment periods that suit your financial goals.",
    },
    {
      icon: <Users className="feature-icon-svg" />,
      title: "Expert Support",
      description:
        "Get personalized investment advice from our certified financial advisors.",
    },
  ];

  const stats = [
    {
      number: "50,000+",
      label: "Happy Investors",
      icon: <Users className="stat-icon-svg" />,
    },
    {
      number: "₹500Cr+",
      label: "Assets Under Management",
      icon: <DollarSign className="stat-icon-svg" />,
    },
    {
      number: "12%",
      label: "Average Returns",
      icon: <TrendingUp className="stat-icon-svg" />,
    },
    {
      number: "99.9%",
      label: "Security Rating",
      icon: <Shield className="stat-icon-svg" />,
    },
  ];

  const benefits = [
    "Zero hidden charges or management fees",
    "Instant online investment process",
    "Regular interest payouts available",
    "Early withdrawal options with minimal penalty",
    "Tax-saving investment options available",
    "24/7 customer support and portfolio tracking",
  ];

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Welcome to <span className="brand-highlight">Azad Finances</span>
            </h1>
            <p className="hero-subtitle">
              Azad Finances specializes in Fixed Deposit (FD) investments
              tailored to secure your capital while delivering competitive
              returns.
            </p>
            <p className="hero-description">
              Join thousands of investors who trust us with their financial
              future. Start your investment journey today with our secure,
              transparent, and high-yielding fixed deposit schemes.
            </p>
            <button onClick={handleNavigate} className="cta-button">
              Go to Your Dashboard
              <ArrowRight className="button-icon" />
            </button>
          </div>
          <div className="hero-visual">
            <div className="floating-card">
              <BarChart3 className="floating-card-icon" />
              <h3 className="floating-card-title">Investment Growth</h3>
              <div className="growth-indicator">
                <span className="growth-percent">+12%</span>
                <span className="growth-label">Annual Returns</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Azad Finances?</h2>
            <p className="section-subtitle">
              Experience the perfect blend of security, growth, and flexibility
              with our fixed deposit solutions.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2 className="benefits-title">
                Maximize Your Investment Potential
              </h2>
              <p className="benefits-subtitle">
                Our fixed deposit schemes are designed to offer maximum returns
                with minimum risk, ensuring your financial goals are always
                within reach.
              </p>
              <div className="benefits-list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <CheckCircle className="benefit-check-icon" />
                    <span className="benefit-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="benefits-visual">
              <div className="benefit-highlight-card">
                <div className="highlight-icon">
                  <Award className="highlight-icon-svg" />
                </div>
                <h3 className="highlight-title">Premium Investment Plans</h3>
                <p className="highlight-description">
                  Tailored solutions for high-net-worth individuals seeking
                  superior returns.
                </p>
                <div className="highlight-stats">
                  <div className="highlight-stat">
                    <span className="stat-value">Up to 12%</span>
                    <span className="stat-desc">Premium Returns</span>
                  </div>
                  <div className="highlight-stat">
                    <span className="stat-value">₹1L+</span>
                    <span className="stat-desc">Minimum Investment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="trust-container">
          <div className="trust-badge">
            <Lock className="trust-badge-icon" />
            <span className="trust-badge-text">Bank-Grade Security</span>
          </div>
          <h2 className="trust-title">Trusted by 50,000+ Investors</h2>
          <p className="trust-description">
            Our commitment to transparency, security, and consistent returns has
            made us the preferred choice for investors across India. With
            regulatory compliance and insurance protection, your investments are
            always safe with us.
          </p>
          <div className="trust-features">
            <div className="trust-feature">
              <div className="trust-feature-icon">
                <Shield className="trust-feature-icon-svg" />
              </div>
              <span className="trust-feature-text">SEBI Registered</span>
            </div>
            <div className="trust-feature">
              <div className="trust-feature-icon">
                <Star className="trust-feature-icon-svg" />
              </div>
              <span className="trust-feature-text">5-Star Rated</span>
            </div>
            <div className="trust-feature">
              <div className="trust-feature-icon">
                <Award className="trust-feature-icon-svg" />
              </div>
              <span className="trust-feature-text">Award Winning</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Start Your Investment Journey?</h2>
          <p className="cta-description">
            Join thousands of smart investors who have already secured their
            financial future with Azad Finances.
          </p>
          <button onClick={handleNavigate} className="cta-button-large">
            Access Your Dashboard
            <ArrowRight className="cta-button-icon" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
